import React, { useState, useEffect, useCallback } from 'react';
import { Card, Collapse, Space, Row, Col, Button, Typography, Tag, Modal, Badge, Progress, Divider, Input, Select, Table, Dropdown } from 'antd';
import { ShopOutlined, SearchOutlined, ClearOutlined, DollarOutlined, ClockCircleOutlined, CheckCircleOutlined, DownOutlined, EyeOutlined, FileTextOutlined } from '@ant-design/icons';
import { useSolicitariAchizitie } from '../hook/useSolicitariAchizitie';
import { useStateContext } from '../../../../../contexts/ContextProvider';
import { Link } from 'react-router-dom';
import FurnizorPDFGenerator from '../FurnizorPage/components/FurnizorPDFGenerator';

const { Text, Title } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

// Componentă pentru filtrele de căutare
const FiltersComponent = ({ 
  localSearchValue, 
  setLocalSearchValue, 
  localOfertsIdsValue,
  setLocalOfertsIdsValue,
  localPurchaseRequestIdsValue,
  setLocalPurchaseRequestIdsValue,
  filters,
  applyFilters,
  productRequestStatuses,
  handleKeyPress,
  handleApplyFilters,
  handleClearFilters,
  loading,
  isMobile 
}) => (
  <Card size="small" style={{ marginBottom: 16 }}>
    <div style={{
      display: 'flex',
      flexWrap: 'nowrap',
      overflowX: 'auto',
      gap: 12,
      alignItems: 'center',
      paddingBottom: 4
    }}>
      <Input
        placeholder="Căutare: furnizor, produs, client..."
        prefix={<SearchOutlined />}
        value={localSearchValue}
        onChange={(e) => setLocalSearchValue(e.target.value)}
        onKeyPress={handleKeyPress}
        allowClear
        onClear={() => setLocalSearchValue('')}
        style={{ minWidth: 220, maxWidth: 300, flex: '0 0 220px' }}
      />
      <Input
        placeholder="ID-uri comenzi (1,2,3...)"
        prefix={<DollarOutlined />}
        value={localOfertsIdsValue}
        onChange={(e) => setLocalOfertsIdsValue(e.target.value)}
        onKeyPress={handleKeyPress}
        allowClear
        onClear={() => setLocalOfertsIdsValue('')}
        style={{ minWidth: 180, maxWidth: 220, flex: '0 0 180px' }}
      />
      <Input
        placeholder="ID-uri cereri achiziție (1,2,3...)"
        prefix={<FileTextOutlined />}
        value={localPurchaseRequestIdsValue}
        onChange={(e) => setLocalPurchaseRequestIdsValue(e.target.value)}
        onKeyPress={handleKeyPress}
        allowClear
        onClear={() => setLocalPurchaseRequestIdsValue('')}
        style={{ minWidth: 180, maxWidth: 220, flex: '0 0 180px' }}
      />
      <Select
        placeholder="Status produse..."
        mode="multiple"
        value={filters.product_in_purchase_statuses}
        onChange={(value) => applyFilters({ ...filters, product_in_purchase_statuses: value })}
        style={{ minWidth: 220, maxWidth: 300, flex: '0 0 220px' }}
        allowClear
        maxTagCount={isMobile ? 1 : 3}
      >
        {productRequestStatuses.map(status => (
          <Option key={status.id} value={status.id}>
            <Tag color={status.color} size="small">{status.label}</Tag>
          </Option>
        ))}
      </Select>
      <Button 
        type="primary"
        icon={<SearchOutlined />} 
        onClick={handleApplyFilters}
        loading={loading}
        style={{ minWidth: 110, flex: '0 0 110px' }}
      >
        Filtrare
      </Button>
      <Button 
        icon={<ClearOutlined />} 
        onClick={handleClearFilters}
        style={{ minWidth: 90, flex: '0 0 90px' }}
      >
        Reset
      </Button>
    </div>
  </Card>
);

function SolicitariAchizitie() {
  // State local
  const [isMobile, setIsMobile] = useState(false);
  const [selectedPurchaseRequest, setSelectedPurchaseRequest] = useState({});
  const [localSearchValue, setLocalSearchValue] = useState('');
  const [localOfertsIdsValue, setLocalOfertsIdsValue] = useState('');
  const [localPurchaseRequestIdsValue, setLocalPurchaseRequestIdsValue] = useState('');

  const { user } = useStateContext();

  // Hook pentru solicitările de achiziție
  const {
    furnizori,
    loading,
    pagination,
    productRequestStatuses,
    purchaseRequestStatuses,
    overallSummary,
    filters,
    applyFilters,
    clearFilters,
    handleTableChange,
    changeProductRequestStatus,
    changePurchaseRequestStatus
  } = useSolicitariAchizitie();

  // Detectare device mobile
  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Sincronizare state local cu filters din hook
  useEffect(() => {
    setLocalSearchValue(filters.search || '');
    setLocalOfertsIdsValue(filters.oferts_ids_list || '');
    setLocalPurchaseRequestIdsValue(filters.purchase_request_ids_list || '');
  }, [filters.search, filters.oferts_ids_list, filters.purchase_request_ids_list]);

  // Utility functions
  const getProductStatusColor = (statusId) => {
    const status = productRequestStatuses.find(option => option.id === statusId);
    return status ? status.color : '#8c8c8c';
  };

  const getProductStatusLabel = (statusId) => {
    const status = productRequestStatuses.find(option => option.id === statusId);
    return status ? status.label : 'Necunoscut';
  };

  const getPurchaseRequestStatusColor = (statusId) => {
    const status = purchaseRequestStatuses.find(option => option.id === statusId);
    return status ? status.color : '#8c8c8c';
  };

  const getPurchaseRequestStatusLabel = (statusId) => {
    const status = purchaseRequestStatuses.find(option => option.id === statusId);
    return status ? status.label : 'Necunoscut';
  };

  // Funcție pentru aplicarea filtrelor (manual trigger)
  const handleApplyFilters = () => {
    const newFilters = {
      ...filters,
      search: localSearchValue,
      oferts_ids_list: localOfertsIdsValue || null,
      purchase_request_ids_list: localPurchaseRequestIdsValue || null
    };
    applyFilters(newFilters);
  };

  // Handler pentru Enter key în input-uri
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  // Clear filters handler
  const handleClearFilters = () => {
    setLocalSearchValue('');
    setLocalOfertsIdsValue('');
    setLocalPurchaseRequestIdsValue('');
    clearFilters();
  };

  // Toggle purchase request details
  const togglePurchaseRequestDetails = (furnizorId, requestId) => {
    const key = `${furnizorId}_${requestId}`;
    setSelectedPurchaseRequest(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Funcție pentru a crea datele furnizorului pentru PDF
  const createFurnizorData = (furnizor) => {
    return {
      denumire: furnizor.furnizor_name,
      brand_name: furnizor.brand_name,
      id_no: furnizor.furnizor_id_no,
      termen_de_plata: furnizor.termen_de_plata || 30,
      termen_de_livrare: furnizor.termen_de_livrare || 7,
      contact: furnizor.contact ? [furnizor.contact] : [],
      adresa_juridica: furnizor.adresa_juridica,
      website: furnizor.website
    };
  };

  // Funcție pentru a grupa produsele pentru PDF
  const createGroupedProducts = (request) => {
    return {
      [request.id]: request.products || []
    };
  };

  // Funcție pentru calcularea totalului unei cereri
  const calculateRequestTotal = (products) => {
    return products.reduce((total, product) => {
      return total + (product.produs_oferta?.bought_price || 0) * (product.produs_oferta?.product_qt || 0);
    }, 0);
  };

  // Calculare sumar pentru un purchase request
  const calculatePurchaseRequestSummary = useCallback((products) => {
    const totalProducts = products.length;
    const statusCounts = products.reduce((acc, product) => {
      const status = productRequestStatuses.find(s => s.id === product.status_id);
      if (status) {
        acc[status.id] = (acc[status.id] || 0) + 1;
      }
      return acc;
    }, {});

    const completedProducts = statusCounts[6] || 0; // Status 6 = gata de ridicare
    const progress = totalProducts > 0 ? (completedProducts / totalProducts) * 100 : 0;

    return {
      totalProducts,
      statusCounts,
      completedProducts,
      progress
    };
  }, [productRequestStatuses]);

  // Calculare sumar pentru un furnizor
  const calculateFurnizorSummary = useCallback((furnizor) => {
    const requests = furnizor.purchase_requests || [];
    const totalRequests = requests.length;
    const allProducts = requests.reduce((acc, req) => acc.concat(req.products || []), []);
    const totalProducts = allProducts.length;
    
    // Calculare statusuri agregate pentru toate produsele furnizorului
    const furnizorStatusCounts = allProducts.reduce((acc, product) => {
      const status = productRequestStatuses.find(s => s.id === product.status_id);
      if (status) {
        acc[status.id] = (acc[status.id] || 0) + 1;
      }
      return acc;
    }, {});
    
    const completedRequests = requests.filter(req => 
      req.products?.every(product => product.status_id >= 7)
    ).length;
    
    const requestProgress = totalRequests > 0 ? (completedRequests / totalRequests) * 100 : 0;

    return {
      totalRequests,
      totalProducts,
      completedRequests,
      requestProgress,
      totalValue: furnizor.summary?.total_sale_value || 0,
      totalProfit: furnizor.summary?.total_profit || 0,
      furnizorStatusCounts
    };
  }, [productRequestStatuses]);

  // Componentă pentru afișarea statusurilor cu contori
  const StatusCountsDisplay = ({ statusCounts, size = 'small', direction = 'horizontal' }) => {
    if (!statusCounts || Object.keys(statusCounts).length === 0) return null;
    
    const statusEntries = Object.entries(statusCounts)
      .map(([statusId, count]) => {
        const status = productRequestStatuses.find(s => s.id === parseInt(statusId));
        return status ? { ...status, count } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.id - b.id);

    const containerStyle = direction === 'vertical' 
      ? { display: 'flex', flexDirection: 'column', gap: '4px' }
      : { display: 'flex', flexWrap: 'wrap', gap: '4px' };

    return (
      <div style={containerStyle}>
        {statusEntries.map(status => (
          <Tag 
            key={status.id} 
            color={status.color} 
            size={size}
            style={{ 
              margin: 0,
              fontSize: size === 'small' ? '11px' : '12px',
              fontWeight: '500'
            }}
          >
            {status.label}: {status.count}
          </Tag>
        ))}
      </div>
    );
  };

  // Handler pentru schimbarea statusului unui produs
  const handleProductStatusChange = async (productId, newStatusId, purchaseRequestId) => {
    await changeProductRequestStatus(productId, newStatusId, purchaseRequestId);
  };

  // Handler pentru schimbarea statusului în bloc pentru un purchase request
  const handleBulkStatusChange = async (furnizorId, requestId, newStatusId) => {
    const furnizor = furnizori.find(f => f.furnizor_id === furnizorId);
    const request = furnizor?.purchase_requests?.find(r => r.id === requestId);
    
    if (request?.products) {
      // Schimbă statusul pentru toate produsele din request
      for (const product of request.products) {
        await changeProductRequestStatus(product.id, newStatusId, requestId);
      }
    }
  };

  // Componentă pentru sumar furnizor
  const FurnizorSummary = ({ furnizor }) => {
    const summary = calculateFurnizorSummary(furnizor);
    
    if (isMobile) {
      return (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Row gutter={[8, 8]}>
            <Col span={8}>
              <div style={{ textAlign: 'center', padding: '8px', background: '#f0f9ff', borderRadius: '6px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>{summary.totalRequests}</div>
                <div style={{ fontSize: '11px', color: '#666' }}>Cereri</div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center', padding: '8px', background: '#f0f9ff', borderRadius: '6px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>{summary.totalProducts}</div>
                <div style={{ fontSize: '11px', color: '#666' }}>Produse</div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center', padding: '8px', background: '#f0f9ff', borderRadius: '6px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: summary.totalProfit >= 0 ? '#52c41a' : '#ff4d4f' }}>
                  {summary.totalProfit.toFixed(0)}
                </div>
                <div style={{ fontSize: '11px', color: '#666' }}>Profit MDL</div>
              </div>
            </Col>
          </Row>
          
          <div style={{ background: '#fafafa', padding: '8px', borderRadius: '6px' }}>
            <div style={{ marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
              Progres: {summary.completedRequests}/{summary.totalRequests}
            </div>
            <Progress 
              percent={Math.round(summary.requestProgress)} 
              size="small" 
              status={summary.requestProgress === 100 ? 'success' : 'active'}
              showInfo={false}
            />
          </div>

          {/* Statusuri furnizor pentru mobile */}
          <div style={{ background: '#f6f8fa', padding: '8px', borderRadius: '6px' }}>
            <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: 'bold', color: '#666' }}>
              Statusuri produse:
            </div>
            <StatusCountsDisplay 
              statusCounts={summary.furnizorStatusCounts}
              size="small"
              direction="horizontal"
            />
          </div>
        </Space>
      );
    }

    return (
      <Space size="large" wrap>
        <Text strong>{summary.totalRequests} cereri</Text>
        <Text strong>{summary.totalProducts} produse</Text>
        <Text strong>Valoare: {summary.totalValue.toFixed(0)} MDL</Text>
        <Text strong style={{ color: summary.totalProfit >= 0 ? '#52c41a' : '#ff4d4f' }}>
          Profit: {summary.totalProfit.toFixed(0)} MDL
        </Text>
        <div>
          <Text strong>Progres: </Text>
          <Progress 
            percent={Math.round(summary.requestProgress)} 
            size="small" 
            status={summary.requestProgress === 100 ? 'success' : 'active'}
            format={() => `${summary.completedRequests}/${summary.totalRequests}`}
          />
        </div>
        
        {/* Statusuri furnizor pentru desktop */}
        <div style={{ marginLeft: 16 }}>
          <StatusCountsDisplay 
            statusCounts={summary.furnizorStatusCounts}
            size="small"
            direction="horizontal"
          />
        </div>
      </Space>
    );
  };

  // Componentă pentru sumar purchase request
  const PurchaseRequestSummary = ({ products }) => {
    const summary = calculatePurchaseRequestSummary(products);
    
    return (
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <div>
          <div style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: 12 }}>
              Finalizate: {summary.completedProducts}/{summary.totalProducts}
            </Text>
          </div>
          <Progress 
            percent={Math.round(summary.progress)} 
            size="small" 
            status={summary.progress === 100 ? 'success' : 'active'}
            showInfo={false}
            strokeColor="#10B981"
          />
        </div>

        <Space size="small" wrap>
          {Object.entries(summary.statusCounts).map(([statusId, count]) => {
            const status = productRequestStatuses.find(s => s.id === parseInt(statusId));
            return status ? (
              <Tag key={statusId} color={status.color} size="small">
                {status.label} ({count})
              </Tag>
            ) : null;
          })}
        </Space>
      </Space>
    );
  };

  // Componentă pentru produse în format tabel/card
  const ProductsDisplay = ({ products, furnizorId, requestId }) => {
    const columns = [
      {
        title: 'Produs',
        dataIndex: ['produs', 'denumire'],
        key: 'product_name',
        width: isMobile ? 200 : 400,
        render: (text, record) => (
          <div style={{ minWidth: isMobile ? 180 : 280 }}>
            <div style={{ marginBottom: 4 }}>
              {record.produs.poza_principala_url && (
                <img 
                  src={`${process.env.REACT_APP_API_URL}${record.produs.poza_principala_url}`}
                  alt={record.produs.denumire}
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    objectFit: 'cover',
                    borderRadius: '4px',
                    float: 'left',
                    marginRight: '8px'
                  }}
                />
              )}
              <div>
                <Text strong style={{ fontSize: isMobile ? 12 : 14 }}>
                  {text?.length > 60 ? `${text.substring(0, 60)}...` : text}
                </Text>
                <Tag className='ml-2' size="small" color="blue">SKU: {record.produs?.sku_intern}</Tag>
              </div>
            </div>
          </div>
        ),
      },
      {
        title: 'Cantitate',
        dataIndex: ['produs_oferta', 'product_qt'],
        key: 'quantity',
        width: 80,
        align: 'center',
        render: (qty, record) => (
          <Text>{qty} {record.produs?.unit_of_measure}</Text>
        )
      },
      {
        title: 'Preț',
        dataIndex: ['produs_oferta', 'bought_price'],
        key: 'price',
        width: 100,
        align: 'center',
        render: (price) => (
          <Text>{price ? `${price} MDL` : '-'}</Text>
        )
      },
      {
        title: 'Total',
        key: 'total',
        width: 100,
        align: 'center',
        render: (_, record) => {
          const total = (record.produs_oferta?.bought_price || 0) * (record.produs_oferta?.product_qt || 1);
          return <Text strong style={{ color: '#1890ff' }}>{total.toFixed(2)} MDL</Text>;
        }
      },
      {
        title: 'Status',
        dataIndex: 'status_id',
        key: 'status',
        width: 150,
        render: (statusId, record) => {
          const statusOptions = productRequestStatuses.map(status => ({
            key: status.id,
            label: (
              <div 
                style={{ 
                  padding: '4px 8px',
                  color: status.color,
                  fontWeight: status.id === statusId ? 'bold' : 'normal'
                }}
                onClick={() => status.id !== statusId && handleProductStatusChange(record.id, status.id, requestId)}
              >
                {status.label}
                {status.id === statusId && ' (actual)'}
              </div>
            ),
            disabled: status.id === statusId
          }));

          return (
            <Dropdown
              menu={{ items: statusOptions }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button 
                style={{ 
                  borderColor: getProductStatusColor(statusId),
                  color: getProductStatusColor(statusId),
                  width: '100%',
                  fontSize: '12px'
                }}
                size="small"
              >
                {getProductStatusLabel(statusId)} <DownOutlined />
              </Button>
            </Dropdown>
          );
        }
      }
    ];

    if (isMobile) {
      return (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {products.map(product => (
            <Card 
              key={product.id}
              size="small"
              style={{ 
                borderLeft: `4px solid ${getProductStatusColor(product.status_id)}`,
                background: '#fafafa'
              }}
            >
              <Row gutter={[8, 8]}>
                <Col span={24}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    {product.produs.poza_principala_url && (
                      <img 
                        src={`${process.env.REACT_APP_API_URL}${product.produs.poza_principala_url}`}
                        alt={product.produs.denumire}
                        style={{ 
                          width: '50px', 
                          height: '50px', 
                          objectFit: 'cover',
                          borderRadius: '6px',
                          marginRight: '12px'
                        }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ fontSize: 14, display: 'block' }}>
                        {product.produs.denumire?.length > 50 
                          ? `${product.produs.denumire.substring(0, 50)}...` 
                          : product.produs.denumire}
                      </Text>
                      <Tag size="small" color="blue">SKU: {product.produs.sku_intern}</Tag>
                    </div>
                  </div>
                  
                  <Row gutter={[8, 8]}>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 'bold' }}>
                          {product.produs_oferta.product_qt}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>
                          {product.produs.unit_of_measure}
                        </div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1890ff' }}>
                          {product.produs_oferta.bought_price || 0}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>MDL</div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 'bold', color: '#52c41a' }}>
                          {((product.produs_oferta.bought_price || 0) * (product.produs_oferta.product_qt || 1)).toFixed(0)}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Total MDL</div>
                      </div>
                    </Col>
                  </Row>

                  <div style={{ marginTop: 12 }}>
                    <Dropdown
                      menu={{
                        items: productRequestStatuses.map(status => ({
                          key: status.id,
                          label: (
                            <div 
                              style={{ 
                                padding: '4px 8px',
                                color: status.color,
                                fontWeight: status.id === product.status_id ? 'bold' : 'normal'
                              }}
                              onClick={() => status.id !== product.status_id && handleProductStatusChange(product.id, status.id, requestId)}
                            >
                              {status.label}
                              {status.id === product.status_id && ' (actual)'}
                            </div>
                          ),
                          disabled: status.id === product.status_id
                        }))
                      }}
                      trigger={['click']}
                      placement="bottomLeft"
                    >
                      <Button 
                        style={{ 
                          borderColor: getProductStatusColor(product.status_id),
                          color: getProductStatusColor(product.status_id),
                          width: '100%',
                          borderRadius: '6px',
                          fontWeight: '500'
                        }}
                        size="small"
                      >
                        {getProductStatusLabel(product.status_id)} <DownOutlined />
                      </Button>
                    </Dropdown>
                  </div>
                </Col>
              </Row>
            </Card>
          ))}
        </Space>
      );
    }

    return (
      <Table
        columns={columns}
        dataSource={products}
        rowKey="id"
        pagination={false}
        scroll={{ x: true }}
        size="small"
      />
    );
  };

  return (
    <div style={{ padding: '12px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Filtre de căutare */}
      <FiltersComponent 
        localSearchValue={localSearchValue}
        setLocalSearchValue={setLocalSearchValue}
        localOfertsIdsValue={localOfertsIdsValue}
        setLocalOfertsIdsValue={setLocalOfertsIdsValue}
        localPurchaseRequestIdsValue={localPurchaseRequestIdsValue}
        setLocalPurchaseRequestIdsValue={setLocalPurchaseRequestIdsValue}
        filters={filters}
        applyFilters={applyFilters}
        productRequestStatuses={productRequestStatuses}
        handleKeyPress={handleKeyPress}
        handleApplyFilters={handleApplyFilters}
        handleClearFilters={handleClearFilters}
        loading={loading}
        isMobile={isMobile}
      />

      {/* Sumar general */}
      {overallSummary && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 8]} align="middle">
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                  {overallSummary.total_furnizori}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Furnizori</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                  {overallSummary.total_requests}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Cereri</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#fa8c16' }}>
                  {overallSummary.total_products}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Produse</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#722ed1' }}>
                  {overallSummary.total_sale_value?.toFixed(0)} MDL
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Valoare</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: overallSummary.total_profit >= 0 ? '#52c41a' : '#ff4d4f' }}>
                  {overallSummary.total_profit?.toFixed(0)} MDL
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Profit</div>
              </div>
            </Col>
            
          </Row>
        </Card>
      )}

      {/* Lista de furnizori */}
      <Card>
        <Title level={4} style={{ marginBottom: 16 }}>
          Solicitări de Achiziție Grupate pe Furnizori ({furnizori.length})
        </Title>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text>Se încarcă...</Text>
          </div>
        ) : (
          <>
            <Collapse 
              size={isMobile ? 'small' : 'default'}
              ghost={isMobile}
            >
              {furnizori.map((furnizor) => (
                <Panel
                  header={
                    <div style={{ width: '100%' }}>
                      {isMobile ? (
                        <div>
                          <div style={{ marginBottom: 8 }}>
                            <Space wrap>
                              <ShopOutlined />
                              <Text strong style={{ fontSize: 14 }}>{furnizor.furnizor_name}</Text>
                              <Badge count={furnizor.purchase_requests?.length || 0} style={{ backgroundColor: '#1890ff' }} />
                            </Space>
                          </div>
                          <FurnizorSummary furnizor={furnizor} />
                        </div>
                      ) : (
                        <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                          <Col flex="auto">
                            <Space wrap>
                              <ShopOutlined />
                              <Text strong>{furnizor.furnizor_name}</Text>
                              <Badge count={furnizor.purchase_requests?.length || 0} style={{ backgroundColor: '#1890ff' }} />
                              <Button 
                                type="link" 
                                size="small" 
                                icon={<EyeOutlined />}
                                onClick={() => window.open(`/furnizorpage/${furnizor.furnizor_id}`, '_blank')}
                              >
                                Pagina Furnizorului
                              </Button>
                            </Space>
                          </Col>
                          <Col flex="none">
                            <FurnizorSummary furnizor={furnizor} />
                          </Col>
                        </Row>
                      )}
                    </div>
                  }
                  key={furnizor.furnizor_id}
                >
                  {/* Purchase Requests pentru furnizor */}
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {furnizor.purchase_requests?.map(request => {
                      const requestKey = `${furnizor.furnizor_id}_${request.id}`;
                      const isExpanded = selectedPurchaseRequest[requestKey];
                      
                      return (
                        <>
                          {/* Mobile version */}
                          <Card 
                            key={`${request.id}-mobile`}
                            size="small"
                            style={{ 
                              border: '1px solid #d9d9d9',
                              borderRadius: '8px'
                            }}
                            title={
                              <div className="block md:hidden">
                                <div style={{ marginBottom: 12 }}>
                                  <Space>
                                    <Button
                                      size="small"
                                      type="primary"
                                      onClick={() => togglePurchaseRequestDetails(furnizor.furnizor_id, request.id)}
                                    >
                                      {isExpanded ? 'Ascunde' : 'Detalii'}
                                    </Button>
                                    <Dropdown
                                      menu={{
                                        items: productRequestStatuses.map(status => ({
                                          key: status.id,
                                          label: (
                                            <div 
                                              style={{ 
                                                padding: '8px 12px',
                                                color: status.color,
                                                fontWeight: 'bold'
                                              }}
                                              onClick={() => handleBulkStatusChange(furnizor.furnizor_id, request.id, status.id)}
                                            >
                                              Toate → {status.label}
                                            </div>
                                          )
                                        }))
                                      }}
                                      trigger={['click']}
                                      placement="bottomLeft"
                                    >
                                      <Button 
                                        size="small" 
                                        type="dashed"
                                        style={{ 
                                          borderColor: '#1890ff',
                                          color: '#1890ff'
                                        }}
                                      >
                                        Status <DownOutlined />
                                      </Button>
                                    </Dropdown>
                                  </Space>
                                </div>
                                
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                  <Space wrap>
                                    <Text strong style={{ color: '#1890ff', fontSize: 14 }}>
                                      {request.client_name}
                                    </Text>
                                    {user?.role_id !== 5 && (
                                      <FurnizorPDFGenerator 
                                        furnizorData={createFurnizorData(furnizor)}
                                        groupedProducts={createGroupedProducts(request)}
                                        productRequestStatuses={productRequestStatuses}
                                        calculateRequestTotal={calculateRequestTotal}
                                        furnizor_id={furnizor.furnizor_id}
                                        isSingleRequest={true}
                                        requestId={request.id}
                                      />
                                    )}
                                    <Tag color="blue" size="small">
                                      #{request.id}
                                    </Tag>
                                    <Tag color="green" size="small">
                                      AMO: {request.amo_id}
                                    </Tag>
                                  </Space>
                                  
                                  <Space wrap>
                                    <Badge 
                                      count={request.products?.length || 0} 
                                      style={{ backgroundColor: '#52c41a' }}
                                      title="Numărul de produse"
                                    />
                                    <Text strong style={{ fontSize: 14 }}>
                                      {request.summary?.total_sale_value?.toFixed(0)} MDL
                                    </Text>

                                    {/* Statusuri produse pentru mobile */}
                                    <div style={{ marginTop: 8 }}>
                                      <StatusCountsDisplay 
                                        statusCounts={calculatePurchaseRequestSummary(request.products || []).statusCounts}
                                        size="small"
                                        direction="horizontal"
                                      />
                                    </div>
                                  </Space>
                                </Space>
                              </div>
                            }
                            className="md:hidden"
                          >
                            {isExpanded && (
                              <div style={{ marginTop: 16  }}>
                                <PurchaseRequestSummary products={request.products || []} />
                                <Divider style={{ margin: '16px 0' }} />
                                <ProductsDisplay 
                                  products={request.products || []} 
                                  furnizorId={furnizor.furnizor_id}
                                  requestId={request.id}
                                />
                              </div>
                            )}
                          </Card>

                          {/* Desktop version */}
                          <Card 
                            key={`${request.id}-desktop`}
                            size="small"
                            style={{ 
                              border: '1px solid #d9d9d9',
                              borderRadius: '8px'
                            }}
                            title={
                              <Row justify="space-between" align="middle">
                                <Col>
                                  <Space>
                                    <Button
                                      size="small"
                                      type="primary"
                                      onClick={() => togglePurchaseRequestDetails(furnizor.furnizor_id, request.id)}
                                    >
                                      {isExpanded ? `#${request.id} Ascunde` : `#${request.id} Detalii`}
                                    </Button>
                                    <Dropdown
                                      menu={{
                                        items: productRequestStatuses.map(status => ({
                                          key: status.id,
                                          label: (
                                            <div 
                                              style={{ 
                                                padding: '8px 12px',
                                                color: status.color,
                                                fontWeight: 'bold'
                                              }}
                                              onClick={() => handleBulkStatusChange(furnizor.furnizor_id, request.id, status.id)}
                                            >
                                              Toate → {status.label}
                                            </div>
                                          )
                                        }))
                                      }}
                                      trigger={['click']}
                                      placement="bottomLeft"
                                    >
                                      <Button 
                                        size="small" 
                                        type="dashed"
                                        style={{ 
                                          borderColor: '#1890ff',
                                          color: '#1890ff'
                                        }}
                                      >
                                        Status în bloc <DownOutlined />
                                      </Button>
                                    </Dropdown>
                                  </Space>
                                </Col>
                                <Col flex="auto">
                                  <Space wrap>
                                    <Text strong style={{ color: '#1890ff', marginLeft: '10px' }}>
                                      {request.client_name}
                                    </Text>
                                    {user?.role_id !== 5 && (
                                      <FurnizorPDFGenerator 
                                        furnizorData={createFurnizorData(furnizor)}
                                        groupedProducts={createGroupedProducts(request)}
                                        productRequestStatuses={productRequestStatuses}
                                        calculateRequestTotal={calculateRequestTotal}
                                        furnizor_id={furnizor.furnizor_id}
                                        isSingleRequest={true}
                                        requestId={request.id}
                                      />
                                    )}
                                    <Tag className='cursor-pointer' onClick={() => window.open(`/dashboard/oferte/${request.oferta_id}`, '_blank')} color='blue'>
                                      Oferta #{request.oferta_id}
                                    </Tag>
                                    <Tag color="green">
                                      AMO: {request.amo_id}
                                    </Tag>
                                    
                                    <Badge 
                                      count={request.products?.length || 0} 
                                      style={{ backgroundColor: '#52c41a' }}
                                      title="Numărul de produse"
                                    />
                                    <Text strong>
                                      {request.summary?.total_sale_value?.toFixed(0)} MDL
                                    </Text>
                                    
                                    {/* Statusuri produse pentru desktop */}
                                    <div style={{ marginLeft: 16 }}>
                                      <StatusCountsDisplay 
                                        statusCounts={calculatePurchaseRequestSummary(request.products || []).statusCounts}
                                        size="small"
                                        direction="horizontal"
                                      />
                                    </div>
                                  </Space>
                                </Col>
                              </Row>
                            }
                            className="hidden md:block"
                          >
                            {isExpanded && (
                              <div style={{ marginTop: 16 }}>
                                <PurchaseRequestSummary products={request.products || []} />
                                <Divider style={{ margin: '16px 0' }} />
                                <ProductsDisplay 
                                  products={request.products || []} 
                                  furnizorId={furnizor.furnizor_id}
                                  requestId={request.id}
                                />
                              </div>
                            )}
                          </Card>
                        </>
                      );
                    })}
                  </Space>
                </Panel>
              ))}
            </Collapse>
            
            {/* Paginație */}
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Space>
                <Button 
                  disabled={pagination.current <= 1}
                  onClick={() => handleTableChange({ ...pagination, current: pagination.current - 1 })}
                >
                  Anterior
                </Button>
                <span>
                  Pagina {pagination.current} din {Math.ceil(pagination.total / pagination.pageSize)}
                </span>
                <Button 
                  disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                  onClick={() => handleTableChange({ ...pagination, current: pagination.current + 1 })}
                >
                  Următor
                </Button>
              </Space>
            </div>
          </>
        )}
      </Card>

      {/* Stiluri CSS pentru componente mobile */}
      <style jsx>{`
        @media (max-width: 768px) {
          .ant-collapse-header {
            padding: 12px 16px !important;
          }
          .ant-card-head {
            padding: 0 12px;
            min-height: 40px;
          }
          .ant-card-body {
            padding: 12px;
          }
          .ant-table {
            font-size: 12px;
          }
          .ant-table-thead > tr > th {
            padding: 8px 4px;
            font-size: 11px;
          }
          .ant-table-tbody > tr > td {
            padding: 8px 4px;
          }
        }
      `}</style>
    </div>
  );
}

export default SolicitariAchizitie;